/**
 * App URLs and company-based routing for the StudyCharm suite.
 * Kept in sync with the same file in studycharm-launch.
 */
export const META_TUTORING_COMPANY_ID = '7f77387b-cf3e-44bf-8bdb-b3b5dd44c6c4';
export const STUDYCHARM_COMPANY_ID = 'c2427ba1-06d1-4386-a8d3-a7e75651c33b';

export const ADMIN_URL = 'https://admin.studycharm.com.au';
export const STUDYCHARM_INTERNAL_ADMIN_URL = 'https://internal.studycharm.com.au';
export const META_TUTORING_ADMIN_URL = 'https://dev.studycharm.com.au';
export const LOCAL_ADMIN_URL = 'http://localhost:5174';
export const LOCAL_STUDYCHARM_INTERNAL_ADMIN_URL = 'http://localhost:5175';
// The student app lives on this origin (and the apex). Meta Tutoring is the
// only company with a separate student deployment.
export const META_TUTORING_STUDENT_URL = 'https://dev-student.studycharm.com.au';
export const PARENT_URL = 'https://parent.studycharm.com.au';

export type Role = 'student' | 'admin' | 'tutor' | 'parent';
/** company_id is null for public self-signup students. */
export type RoleRow = { role: Role; company_id: string | null };

export function isRunningOnLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

export function isMetaTutoringCompany(companyId: string): boolean {
  return companyId === META_TUTORING_COMPANY_ID;
}

export function isStudyCharmCompany(companyId: string): boolean {
  return companyId === STUDYCHARM_COMPANY_ID;
}

/** Resolve admin app URL from a user's staff roles (admin/tutor). */
export function getAdminUrlForRoles(roles: RoleRow[]): string {
  const staffCompanyIds = roles
    .filter((r) => r.role === 'admin' || r.role === 'tutor')
    .map((r) => r.company_id)
    .filter((id): id is string => !!id);

  if (isRunningOnLocalhost()) {
    if (staffCompanyIds.some(isStudyCharmCompany)) {
      return LOCAL_STUDYCHARM_INTERNAL_ADMIN_URL;
    }
    return LOCAL_ADMIN_URL;
  }

  if (staffCompanyIds.some(isStudyCharmCompany)) {
    return STUDYCHARM_INTERNAL_ADMIN_URL;
  }

  if (staffCompanyIds.some(isMetaTutoringCompany)) {
    return META_TUTORING_ADMIN_URL;
  }

  return ADMIN_URL;
}

/**
 * Students log in here and stay here, so a handoff is only ever about moving
 * them to a deployment that serves their company specifically. Returns null
 * when they should stay in this app.
 *
 * Meta Tutoring is the only company with its own student deployment. Everyone
 * else stays on whichever origin they signed in on — including public signups,
 * who have no company at all. Falling back to a "canonical" student URL for
 * them is what sent self-signups to a domain with no deployment behind it.
 */
export function getStudentHandoffUrl(roles: RoleRow[]): string | null {
  // Every deployment is reachable as localhost in dev, so never bounce.
  if (isRunningOnLocalhost()) return null;

  const servedByMetaTutoring = roles.some(
    (r) => r.role === 'student' && !!r.company_id && isMetaTutoringCompany(r.company_id),
  );
  if (!servedByMetaTutoring) return null;

  return window.location.origin === META_TUTORING_STUDENT_URL
    ? null
    : META_TUTORING_STUDENT_URL;
}
